import type { AllowancesTable } from 'components/allowances/dashboard/columns';
import Button from 'components/common/Button';
import Modal from 'components/common/Modal';
import { useRevokeBatch } from 'lib/hooks/ethereum/useRevokeBatch';
import { useAddress } from 'lib/hooks/page-context/AddressIdentityContext';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo } from 'react';
import ControlsWrapper from '../ControlsWrapper';
import BatchRevokeControls from './BatchRevokeControls';
import BatchRevokeHeader from './BatchRevokeHeader';
import BatchRevokeTable from './BatchRevokeTable';

interface Props {
  table: AllowancesTable;
  open: boolean;
  setOpen: (open: boolean) => void;
}

const BatchRevokeModalWithButton = ({ table, open, setOpen }: Props) => {
  const t = useTranslations();
  const { address } = useAddress();

  // biome-ignore lint/correctness/useExhaustiveDependencies: we only want to refresh the selected allowances when the modal is opened/closed
  const selectedAllowances = useMemo(() => {
    return table.getGroupedSelectedRowModel().flatRows.map((row) => row.original);
  }, [open]);

  const selectedChainId = selectedAllowances[0]?.chainId ?? 1;

  const { results, revoke, pause, isSubmitting, isRevoking, isAllConfirmed, feeDollarAmount, progress } =
    useRevokeBatch(selectedAllowances, table.options.meta!.onUpdate);

  useEffect(() => {
    if (!open) pause();
  }, [open, pause]);

  const totalRevoked = Object.values(results).filter((result) => result.status === 'confirmed').length;
  const totalReverted = Object.values(results).filter((result) => result.status === 'reverted').length;

  if (!selectedAllowances || !results) return null;

  return (
    <>
      <ControlsWrapper address={address} overrideDisabled={!table.getIsSomeRowsSelected()}>
        {(disabled) => (
          <div className="w-fit">
            <Button style="primary" size="sm" disabled={disabled} onClick={() => setOpen(true)}>
              {t('common.buttons.revoke_selected')}
            </Button>
          </div>
        )}
      </ControlsWrapper>
      <Modal open={open} setOpen={setOpen} className="sm:max-w-5xl min-h-[60vh] overflow-hidden" onlyExplicitClose>
        <div className="flex flex-col justify-between gap-4">
          <div>
            <BatchRevokeHeader
              totalRevoked={totalRevoked}
              totalReverted={totalReverted}
              totalSelected={selectedAllowances.length}
            />
            <div className="h-[46vh] w-full overflow-scroll whitespace-nowrap scrollbar-hide">
              <BatchRevokeTable selectedAllowances={selectedAllowances} results={results} />
            </div>
          </div>
          <BatchRevokeControls
            chainId={selectedChainId}
            allowanceCount={selectedAllowances.length}
            feeDollarAmount={feeDollarAmount}
            isRevoking={isSubmitting || isRevoking}
            isAllConfirmed={isAllConfirmed}
            setOpen={setOpen}
            revoke={revoke}
            progress={progress}
          />
        </div>
      </Modal>
    </>
  );
};

export default BatchRevokeModalWithButton;
